import React, { useEffect, useState } from 'react';
import { BirdzNotif, BirdzTitle, useNotif } from '@applications-terrains/birdz-react-library';
import { Alert, Box, Button, Grid, IconButton, TextField } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import axios, { AxiosError } from 'axios';
import { Form, Formik, FormikValues } from 'formik';
import { useGetCompanies } from '../../../../hooks/datarefs';
import {
  ModalWithLoaderAction,
  RequestStatus
} from '../../../Tools/ModalWithLoader/ModalWithLoader';
import { useTranslation } from 'react-i18next';
import SelectWithSearchModal from '../../../Tools/Selects/SelectWithSearchModal';

export default function FP2EDiameterForm({
  endpoint,
  setRequestStatus,
  action,
  setAction,
  id
}: {
  endpoint: string;
  setRequestStatus: (value: RequestStatus) => void;
  action: ModalWithLoaderAction;
  setAction: (value: ModalWithLoaderAction) => void;
  id: number | null;
}) {
  const { t } = useTranslation();
  const { notif, notifOptions } = useNotif();
  const [values, setValues] = useState<FormikValues>();
  const [isError, setIsError] = useState<boolean>();
  const initialValues = {
    Code: null,
    Value: null,
    MeterKey: null,
    companies: []
  };
  const { data: clients } = useGetCompanies();
  const [error, setError] = useState<Record<string, string>>();

  useEffect(() => {
    if (action === 'edit' && id !== null) {
      axios.get(endpoint + `${id}/`).then((response) => {
        setValues(response.data);
      });
    }
  }, [action, id]);

  const handleSubmit = async (values: FormikValues) => {
    const payload = values;

    if (!id) {
      await axios
        .post(endpoint, payload)
        .then(() => {
          setRequestStatus('success');
          setTimeout(() => {
            setRequestStatus(null);
            setAction(null);
          }, 1500);
        })
        .catch((error) => {
          const result = error.response.data as Record<string, string>;
          setError(result);
          {
            !result &&
              notif({
                content: t('anErrorOccurred'),
                type: 'error'
              });
          }
        });
    } else {
      await axios
        .put(endpoint + `${id}`, payload)
        .then(() => {
          setRequestStatus('success');
          setTimeout(() => {
            setRequestStatus(null);
            setAction(null);
          }, 1500);
        })
        .catch((error: AxiosError<Record<string, string>>) => {
          const result = error.response?.data;
          setError(result);
          {
            !error &&
              notif({
                content: t('anErrorOccurred'),
                type: 'error'
              });
          }
        });
    }
  };

  return (
    <>
      <BirdzTitle>{t('referencesTables.fp2e.diameter')}</BirdzTitle>
      <Formik
        initialValues={values ? values : initialValues}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
        enableReinitialize={true}
      >
        {({ values, setFieldValue }) => {
          const needRequiredFields = !values.Code || !values.Value || !values.MeterKey;
          setIsError(needRequiredFields);
          return (
            <Form>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      size="small"
                      type={'text'}
                      name={'Code'}
                      value={values.Code || ''}
                      fullWidth
                      onChange={(e) => {
                        setFieldValue('Code', e.target.value);
                      }}
                      label={t('referencesTables.codeIndication') + '*'}
                    />
                    {values.Code && (
                      <IconButton
                        size="small"
                        onClick={() => setFieldValue('Code', null)}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {error && 'Code' in error && (
                    <Alert sx={{ mt: 1 }} severity="error">
                      {error['Code']}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      size="small"
                      type="number"
                      name={'Value'}
                      value={values.Value || ''}
                      fullWidth
                      onChange={(e) => {
                        const value = e.target.value;

                        // Accepte seulement les entiers positifs
                        if (value === '' || (/^\d+$/.test(value) && parseInt(value) > 0)) {
                          setFieldValue('Value', value);
                        }
                      }}
                      onKeyDown={(e) => {
                        // Bloque les touches: -, +, e, E, .  ,
                        if (['-', '+', 'e', 'E', '.', ','].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      inputProps={{
                        min: 1,
                        step: 1
                      }}
                      label={t('referencesTables.value') + '*'}
                    />
                    {values.Value && (
                      <IconButton
                        size="small"
                        onClick={() => setFieldValue('Value', null)}
                        sx={{
                          position: 'absolute',
                          right: 32,
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {error && error.Value && (
                    <Alert sx={{ mt: 1 }} severity="error">
                      {error?.Value}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      size="small"
                      type="number"
                      name="MeterKey"
                      value={values.MeterKey || ''}
                      fullWidth
                      onChange={(e) => {
                        const value = e.target.value;

                        // Accepte seulement les entiers positifs
                        if (value === '' || (/^\d+$/.test(value) && parseInt(value) > 0)) {
                          setFieldValue('MeterKey', value);
                        }
                      }}
                      onKeyDown={(e) => {
                        // Bloque les touches: -, +, e, E, .  ,
                        if (['-', '+', 'e', 'E', '.', ','].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      inputProps={{
                        min: 1,
                        step: 1
                      }}
                      label={t('referencesTables.meterKey') + '*'}
                    />
                    {values.MeterKey && (
                      <IconButton
                        size="small"
                        onClick={() => setFieldValue('MeterKey', null)}
                        sx={{
                          position: 'absolute',
                          right: 32,
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {error && error.MeterKey && (
                    <Alert sx={{ mt: 1 }} severity="error">
                      {error?.MeterKey}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <SelectWithSearchModal
                    multiple
                    label={t('referencesTables.clients')}
                    options={(clients ?? []).map((c) => ({ label: c.Name, value: c.Id }))}
                    value={values.companies ?? []}
                    onChange={(val) => setFieldValue('companies', val)}
                  />
                </Grid>
              </Grid>

              <Grid container justifyContent="center" sx={{ mt: 2 }}>
                <Grid item>
                  <Button
                    sx={{ ml: 2 }}
                    onClick={() => {
                      setRequestStatus(null);
                      setAction(null);
                    }}
                  >
                    {t('referencesTables.cancel')}
                  </Button>
                  <Button type="submit" variant="contained" disabled={isError}>
                    {action === 'edit' ? t('referencesTables.edit') : t('referencesTables.add')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>

      <BirdzNotif options={notifOptions} />
    </>
  );
}
